import { CompanyDocumentType } from 'contracts/Common';
import { isPast, isToday } from 'date-fns';

export class Validator {
  public static isValidCNPJ(cnpj: string): boolean {
    const cleanCnpj = cnpj.replace(/[^\d]+/g, '');

    if (cleanCnpj.length !== 14) {
      return false;
    }

    const invalidOnes: Array<string> = [
      '00000000000000',
      '11111111111111',
      '22222222222222',
      '33333333333333',
      '44444444444444',
      '55555555555555',
      '66666666666666',
      '77777777777777',
      '88888888888888',
      '99999999999999',
    ];

    if (invalidOnes.includes(cleanCnpj)) {
      return false;
    }

    const getVerifierDigit = (digits: string): number => {
      let index: number = 2;
      const numbers: number[] = Array.from(digits).map(Number);
      const reverse: number[] = numbers.reverse();

      const sum: number = reverse.reduce((acc, curr) => {
        acc += curr * index;
        index = index === 9 ? 2 : index + 1;
        return acc;
      }, 0);

      const mod: number = sum % 11;
      return mod < 2 ? 0 : 11 - mod;
    };

    let numbers: string = cleanCnpj.substring(0, 12);
    numbers += getVerifierDigit(numbers);
    numbers += getVerifierDigit(numbers);

    return (
      numbers.substring(numbers.length - 2) ===
      cleanCnpj.substring(cleanCnpj.length - 2)
    );
  }

  public static isValidCPF(cpf: string): boolean {
    const cleanCpf = cpf.replace(/[^\d]+/g, '');

    if (cleanCpf.length !== 11) {
      return false;
    }

    const invalidOnes: Array<string> = [
      '00000000000',
      '11111111111',
      '22222222222',
      '33333333333',
      '44444444444',
      '55555555555',
      '66666666666',
      '77777777777',
      '88888888888',
      '99999999999',
    ];

    if (invalidOnes.includes(cleanCpf)) {
      return false;
    }

    const getVerifierDigit = (digits: string): number => {
      const numbers: number[] = Array.from(digits).map(Number);
      const multiplied: number[] = numbers.map(
        (n, i) => n * (numbers.length + 1 - i)
      );
      const mod: number = multiplied.reduce((acc, curr) => acc + curr) % 11;
      return mod < 2 ? 0 : 11 - mod;
    };

    let numbers: string = cleanCpf.substring(0, 9);
    numbers += getVerifierDigit(numbers);
    numbers += getVerifierDigit(numbers);

    return (
      numbers.substring(numbers.length - 2) ===
      cleanCpf.substring(cleanCpf.length - 2)
    );
  }

  public static isValidCnpjAlfanumerico = (value: any) => {
    if (!value) return false;
    const clean = value
      .toString()
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '');
    if (clean.length !== 14) return false;
    if (/^([A-Z0-9])\1+$/.test(clean)) return false;
    const charToValue = (char: string) => {
      return char.charCodeAt(0) - 48;
    };
    const values = clean.split('').map(charToValue);
    const calcDigit = (base: number[]) => {
      let factor = base.length - 7;
      let total = 0;
      for (let i = 0; i < base.length; i++) {
        total += base[i] * factor--;
        if (factor < 2) {
          factor = 9;
        }
      }
      const remainder = total % 11;
      return remainder < 2 ? 0 : 11 - remainder;
    };
    const base12 = values.slice(0, 12);
    const dv1 = values[12];
    const dv2 = values[13];
    const calcDv1 = calcDigit(base12);
    if (calcDv1 !== dv1) return false;
    const calcDv2 = calcDigit([...base12, calcDv1]);
    if (calcDv2 !== dv2) return false;
    return true;
  };

  /**
   * This one will validate both CPF and CNPJ
   * and will throw an error if the document is invalid
   */
  public static validateDocument(
    document: string,
    documentType: CompanyDocumentType
  ): void {
    if (documentType === 'cnpj') {
      if (!document) throw new Error('Informe o CNPJ');
      if (
        !this.isValidCNPJ(document) &&
        !this.isValidCnpjAlfanumerico(document)
      )
        throw new Error('CNPJ inválido');
    }

    if (documentType === 'cpf') {
      if (!document) throw new Error('Informe o CPF');
      if (!this.isValidCPF(document)) throw new Error('CPF inválido');
    }

    if (!document) throw new Error('Informe o documento');
  }

  public static dateIsPast(date: Date) {
    if (isNaN(new Date(date).getTime())) {
      throw new Error('Data inválida');
    }

    if (isPast(date) && !isToday(date)) {
      throw new Error('A data não pode estar no passado');
    }

    return true;
  }
}
